import { DataType, DateTime, ExprCall, ExprForm } from '@mode-switch/express';
import { Attr } from '@mode-switch/model';
import { DatePart, EncField, EncFieldGroup, Encoding, VariableType } from '@mode-vegas/vegas';
import { ChartTypes, FlamingoTypes } from '@mode/shared/contract-common';
import {
  canColTypeUseFormatter,
  financialFormatter,
  formatInUTC,
  getDefaultTextFormat,
  getTextFormatByCurrencyPreset,
  getTextFormatOptionsByShortcutType,
  numberFormatter,
  percentFormatter,
} from '@mode/shared/util-js';
import { CompiledField, Switch } from '@mode/shared/util-switch';
import { ChartDate } from './helpers';
import { TOTALS_TOKEN } from './total-helpers';

export const MISSING_VALUE = 'null';

export function isMissingValue(value: any): boolean {
  return (
    value == null ||
    value === 'null' ||
    (typeof value === 'object' &&
      Object.prototype.hasOwnProperty.call(value, '_value') &&
      (value._value === 'null' || Number.isNaN(value._value)))
  );
}

export function formatValue(value: any, compiled: CompiledField, format?: ChartTypes.TextFormat) {
  const { hasDatePart, isDiscrete, formulaDataType } = compiled;

  format = resolveTextFormatForCompiledField(compiled, format);

  switch (formulaDataType) {
    case DataType.Name.DateTime:
    case DataType.Name.Timestamp: {
      return dateTypeFormat(value, format, ChartDate.getDatePart(compiled), compiled);
    }
    case DataType.Name.Numeric:
    case DataType.Name.Integer:
    case DataType.Name.Float:
    case DataType.Name.Decimal: {
      if (hasDatePart && isDiscrete) {
        return datePartFormat(value, format, ChartDate.getDatePart(compiled), compiled);
      }

      // As of 4/22, the flat tables below Visual Explorer provide no way to change the format.
      // We can't apply additional formatting there unless we give the user a way to change it.
      // When the the format is 'Auto', and the number is not discrete, this applies additional formatting in Visual Explorer.
      return numberTypeFormat(value, format, !isDiscrete);
    }
    default: {
      return value === TOTALS_TOKEN ? 'All' : value;
    }
  }
}

export function resolveTextFormatForCompiledField(compiled: CompiledField, format?: ChartTypes.TextFormat) {
  let { defaultFormat } = compiled;

  ///
  /// Resolve default format

  if (defaultFormat?.type) {
    switch (compiled.form) {
      /**
       * Pct* formulas should only apply PercentType default formats
       */
      case ExprForm.PctDifference:
      case ExprForm.PctOfTotal: {
        if (defaultFormat.type !== ChartTypes.FormatType.Percent) {
          defaultFormat = undefined;
        }
        break;
      }
      /**
       * COUNT-ed formulas should only apply NumberType default formats.
       * We're interested in digit separators, digit grouping, etc. but
       * unset precision since COUNT-ed fields always produce an int
       */
      case ExprForm.Aggregate: {
        switch (compiled.aggFunc) {
          case ExprCall.Name.Count:
          case ExprCall.Name.CountD:
          case ExprCall.Name.CountDA: {
            if (defaultFormat.type === ChartTypes.FormatType.Number) {
              defaultFormat = { ...defaultFormat, precision: undefined };
            } else {
              defaultFormat = undefined;
            }
          }
        }
        break;
      }
    }
  }

  ///
  /// Resolve applied format

  let appliedFormat = format;

  /**
   * Fall back to the default format if the provided format is undefined or Automatic
   */
  if (isAutomaticShortcutType(appliedFormat?.shortcutType)) {
    appliedFormat = defaultFormat || format;
  }

  /**
   * Checks if the applied format type is valid for the formula type. If it's not,
   * we want to compute the format type based on the formula's data type
   */
  if (appliedFormat) {
    if (!canColTypeUseFormatter(appliedFormat.type, compiled.formulaDataType ?? DataType.Name.Null)) {
      appliedFormat = undefined;
    }
  }

  return appliedFormat;
}

export function getDefaultFieldFormat(): ChartTypes.FieldFormat {
  return {
    paneFormat: {},
    axisFormat: {},
    headerFormat: {},
    legendFormat: {},
  };
}

export function makeDefaultTextFormat(): ChartTypes.TextFormat {
  return {
    type: ChartTypes.FormatType.Default,
    // Defaults are so number dropdowns have values
    grouping: 3,
    negativeBehavior: 'minusSign',
    decimalStyle: '.',
    separator: ',',
    currencyPreset: ChartTypes.CurrencyPreset.USD,
  };
}

export function setCurrencyPreset(format: ChartTypes.TextFormat, preset: ChartTypes.CurrencyPreset) {
  if (preset !== ChartTypes.CurrencyPreset.Custom) {
    return {
      ...format,
      ...getTextFormatByCurrencyPreset(preset),
    };
  }

  // Don't override anything for custom preset
  return format;
}

export const enum FormatMapKeyPrefix {
  DISCRETE = '@DIS_',
  CONTINUOUS = '@CON_',
}

export function getFormatMapKeyPrefix(type: VariableType): FormatMapKeyPrefix {
  return type === VariableType.DISCRETE ? FormatMapKeyPrefix.DISCRETE : FormatMapKeyPrefix.CONTINUOUS;
}

export function getFormatMapKey(formula: string, type: VariableType): string {
  return `${getFormatMapKeyPrefix(type)}${formula}`;
}

export function getFormatMapKeyByField(field: EncField): string {
  return getFormatMapKey(field.formula, field.variable.type);
}

export function getFormatMapKeyByGroup(group: EncFieldGroup): string {
  return getFormatMapKey(group.id, VariableType.CONTINUOUS);
}

export function getFormatMapKeyForModeField(field: FlamingoTypes.ModeField): string {
  return getFormatMapKey(field.formula.source, Switch.Compat.makeVariableType(field.type));
}

export function buildQuickCalcPercentFormat(field: EncField) {
  const fieldFormats: ChartTypes.FieldFormatMap = {};
  const percentScale = {
    ...getTextFormatOptionsByShortcutType(ChartTypes.FormatShortcutType.Percent),
    type: ChartTypes.FormatType.Percent,
    shortcutType: ChartTypes.FormatShortcutType.Percent,
    precision: 2,
  };

  const key = getFormatMapKeyByField(field);
  fieldFormats[key] = {
    ...getDefaultFieldFormat(),
    paneFormat: {
      [ChartTypes.FieldPaneTypes.Default]: percentScale,
    },
    axisFormat: {
      [ChartTypes.FieldAxisTypes.Scale]: {
        [ChartTypes.FieldAxisTypes.Default]: percentScale,
      },
    },
  };
  return fieldFormats;
}

export function datePartFormat(
  value: any,
  formatOptions: ChartTypes.TextFormat | undefined,
  datePart?: DatePart | null,
  compiled?: CompiledField
): string {
  // Check for missing value.
  if (isMissingValue(value)) {
    return MISSING_VALUE;
  }

  // Filter out invalid date part
  if (Number.isNaN(+value)) {
    return 'Invalid Date';
  }

  switch (datePart) {
    case DatePart.YEAR:
      return datePartYear(value, formatOptions, compiled);
    case DatePart.QUARTER:
      return datePartQuarter(value, formatOptions);
    case DatePart.MONTH:
      return datePartMonth(value, formatOptions);
    case DatePart.WEEKDAY:
      return datePartWeekday(value, formatOptions);
    case DatePart.DAY:
      return datePartDay(value, formatOptions);
    case DatePart.HOUR:
      return datePartHour(value, formatOptions);
    case DatePart.MINUTE:
    case DatePart.SECOND: {
      if (formatOptions?.shortcutType === ChartTypes.FormatShortcutType.Ordinal) {
        return formatOrdinal(+value);
      }
      return '' + value;
    }
    default: {
      return '' + value;
    }
  }
}

function datePartYear(value: any, formatOptions: ChartTypes.TextFormat | undefined, compiled?: CompiledField): string {
  const prefix =
    DateTime.yearStartMonth(compiled?.yearStart ?? compiled?.defaultYearStart ?? DateTime.YearStart.January) > 1
      ? 'FY '
      : '';
  const fullyear = '' + value;
  return (
    prefix +
    (fullyear.length > 2 && formatOptions?.shortcutType === ChartTypes.FormatShortcutType.ShortName
      ? fullyear.slice(fullyear.length - 2)
      : fullyear)
  );
}

function datePartQuarter(value: any, formatOptions: ChartTypes.TextFormat | undefined): string {
  switch (formatOptions?.shortcutType) {
    case ChartTypes.FormatShortcutType.FullName:
      return ChartDate.makeDatePartQuarterFullName(value);
    case ChartTypes.FormatShortcutType.Numeric:
      return '' + value;
    case ChartTypes.FormatShortcutType.Ordinal:
      return formatOrdinal(+value);
    default:
      return ChartDate.makeDatePartQuarterName(value);
  }
}

function datePartMonth(value: any, formatOptions: ChartTypes.TextFormat | undefined): string {
  switch (formatOptions?.shortcutType) {
    case ChartTypes.FormatShortcutType.Numeric:
      return '' + value;
    case ChartTypes.FormatShortcutType.Ordinal:
      return formatOrdinal(+value);
    case ChartTypes.FormatShortcutType.ShortName: {
      const fullmonth = ChartDate.makeDatePartMonthName(value);
      return fullmonth.substring(0, 3);
    }
    default: {
      return ChartDate.makeDatePartMonthName(value);
    }
  }
}

function datePartWeekday(value: any, formatOptions: ChartTypes.TextFormat | undefined): string {
  switch (formatOptions?.shortcutType) {
    case ChartTypes.FormatShortcutType.Numeric:
      return '' + (+value + 1);
    case ChartTypes.FormatShortcutType.Ordinal:
      return formatOrdinal(+value + 1);
    case ChartTypes.FormatShortcutType.ShortName: {
      const fullmonth = ChartDate.makeDatePartWeekdayName(value);
      return fullmonth.substring(0, 3);
    }
    default: {
      return ChartDate.makeDatePartWeekdayName(value);
    }
  }
}

function datePartDay(value: any, formatOptions: ChartTypes.TextFormat | undefined): string {
  if (formatOptions?.shortcutType === ChartTypes.FormatShortcutType.Ordinal) {
    return formatOrdinal(+value);
  }
  return '' + value;
}

function datePartHour(value: any, formatOptions: ChartTypes.TextFormat | undefined): string {
  if (formatOptions?.shortcutType === ChartTypes.FormatShortcutType.Numeric) {
    return '' + value;
  }
  if (formatOptions?.shortcutType === ChartTypes.FormatShortcutType.Ordinal) {
    return formatOrdinal(+value);
  }
  // 24h assersion: from the DB, the range is 0 - 23
  const hour24 = Math.max(0, Math.min(0 | value, 23));
  // Get the ante meridiem | post meridiem part
  const aa = hour24 >= 12 ? 'pm' : 'am';
  // Normalize the hour range: 1 - 12
  const hour = hour24 % 12 || 12;
  // Return "1pm", "11am", etc
  return formatOptions?.shortcutType === ChartTypes.FormatShortcutType.FullName
    ? `${hour} ${aa.toLocaleUpperCase()}`
    : `${hour}${aa}`;
}

export function dateTypeFormat(
  value: any,
  formatOptions: ChartTypes.TextFormat | undefined,
  datePart?: DatePart | null,
  compiled?: CompiledField
): string {
  // Check for missing value.
  if (isMissingValue(value)) {
    return MISSING_VALUE;
  }

  // Attempt coercing non-numbers to numbers
  if (typeof value !== 'number') {
    // Basic coercion to number, works for ms or Date instances
    const asNumber = +value;
    value = !Number.isNaN(asNumber)
      ? asNumber
      : /**
         * Parses a string representation of a date, returning
         * the number of milliseconds since January 1, 1970, 00:00:00 UTC
         * or NaN if the string is unrecognized.
         *
         * Works for strings of the form:
         * "2019-01-01"
         * "2019-01-01T00:00:00.000Z"
         * "2019-01-01T00:00:00.000+00:00"
         */
        (value = Date.parse('' + value));
  }

  // Filter out what we couldn't coerce
  if (Number.isNaN(value)) {
    return 'Invalid Date';
  }

  if (
    formatOptions?.type === ChartTypes.FormatType.Date &&
    formatOptions.shortcutType !== ChartTypes.FormatShortcutType.Automatic
  ) {
    const dateFormat = formatOptions.dateFormat as ChartTypes.DateFormat;
    if (dateFormat === 'year') {
      return ChartDate.formatDateTruncYear(value, compiled?.yearStart ?? compiled?.defaultYearStart);
    }

    const mask = ChartDate.DateFormatStrings[dateFormat];
    if (typeof mask === 'string') {
      return formatInUTC(new Date(value), mask);
    }
  }

  if (datePart) {
    return dateTruncFormat(value, datePart, compiled);
  }

  return formatInUTC(new Date(value), 'MM/dd/y');
}

export function dateTruncFormat(value: number, datePart: DatePart, compiled?: CompiledField): string {
  const yearStart = compiled?.yearStart ?? compiled?.defaultYearStart;
  const date = new Date(value);

  switch (datePart) {
    case DatePart.YEAR:
      return ChartDate.formatDateTruncYear(date, yearStart);
    case DatePart.QUARTER:
      return ChartDate.formatDateTruncQuarter(date, yearStart);
    case DatePart.MONTH:
      return formatInUTC(date, 'MMM y');
    case DatePart.WEEK:
    case DatePart.WEEKDAY:
    case DatePart.DAY:
    case DatePart.DAYOFYEAR:
      return formatInUTC(date, 'MMM d y');
    case DatePart.HOUR:
      return formatInUTC(date, 'hhaa MMM d y');
    case DatePart.MINUTE:
      return formatInUTC(date, 'hh:mmaa MMM d y');
    case DatePart.SECOND:
      return formatInUTC(date, 'hh:mm:ssaa MMM d y');
    default:
      return formatInUTC(date, 'MM/dd/y');
  }
}

/**
 * Formats numbers, including currency and percentages.
 *
 * @param {any} value value
 * @param {TextFormat} format format options
 * @param {boolean} isAutoFormattable true iff additional formatting can be applied to the default 'Auto' format
 */
export function numberTypeFormat(value: any, format?: ChartTypes.TextFormat, isAutoFormattable?: boolean): string {
  if (value == null) {
    return value;
  }

  // Check for missing value.
  if (isMissingValue(value)) {
    return MISSING_VALUE;
  }

  const safeNumber = typeof value === 'string' ? parseFloat(value) : value;
  if (typeof safeNumber !== 'number' || Number.isNaN(safeNumber)) {
    return value;
  }

  if (format == null) {
    return numberFormatter(safeNumber, getDefaultTextFormat(DataType.Name.Float), isAutoFormattable);
  }
  switch (format.type) {
    case ChartTypes.FormatType.Number:
      return numberFormatter(safeNumber, format, isAutoFormattable);
    case ChartTypes.FormatType.Financial:
      return financialFormatter(safeNumber, format);
    case ChartTypes.FormatType.Percent:
      return percentFormatter(safeNumber, format);
    default:
      return value;
  }
}

/**
 * Returns the ordinal form (0th, 1st, 2nd, etc) of the specified value.
 * Currently, only supports values from 0-99 which is sufficient for
 * date part formatting.
 */
function formatOrdinal(value: number) {
  if (value < 0 || value > 99) {
    return '' + value;
  }
  const suffix = (val: number) => {
    if (val > 3 && val < 21) return 'th';
    switch (val % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };
  const floored = Math.floor(value);
  return `${floored}${suffix(floored)}`;
}

/**
 * Determines the most appropriate FieldFormatType based on where the field is located
 * in the encoding. The FieldFormatType is based on the field variable type and whether
 * it is located on a positional or mark channel
 * @param encoding
 * @param encField
 * @returns the FieldFormatType which is applicable based on the field's usage in the encoding.
 * If the field is not in the encoding, it returns Pane by default.
 */
export function determineFieldFormatTypeForField(encoding: Encoding, encField: EncField) {
  if (encField.variable.type === VariableType.CONTINUOUS) {
    const axisFields = [...encoding.x, ...encoding.x2, ...encoding.y, ...encoding.y2];
    return axisFields.some((field) => field.id === encField.id)
      ? ChartTypes.FieldFormatTypes.Axis
      : ChartTypes.FieldFormatTypes.Pane;
  }
  const positionalFields = [...encoding.column, ...encoding.row, ...encoding.x, ...encoding.y];
  return positionalFields.some((field) => field.id === encField.id)
    ? ChartTypes.FieldFormatTypes.Header
    : ChartTypes.FieldFormatTypes.Pane;
}

/**
 * Returns the appropriate TextFormat to apply to the axis.
 */
export function resolveAxisTextFormat(format?: ChartTypes.FieldFormat) {
  return resolveTextFormat(format, ChartTypes.FieldFormatTypes.Axis, ChartTypes.FieldFormatTypes.Pane);
}

/**
 * Returns the appropriate TextFormat to apply to the header.
 */
export function resolveHeaderTextFormat(format?: ChartTypes.FieldFormat) {
  return resolveTextFormat(format, ChartTypes.FieldFormatTypes.Header, ChartTypes.FieldFormatTypes.Pane);
}

/**
 * Returns the appropriate TextFormat to apply to the pane.
 */
export function resolvePaneTextFormat(format: ChartTypes.FieldFormat, altFormatType: ChartTypes.FieldFormatTypes) {
  return resolveTextFormat(format, ChartTypes.FieldFormatTypes.Pane, altFormatType);
}

export function resolvePaneTextFormatForField(
  fieldFormats: ChartTypes.FieldFormatMap | undefined,
  field: FlamingoTypes.ModeField | undefined
) {
  if (fieldFormats === undefined || field === undefined) {
    return makeDefaultTextFormat();
  }
  const formatKey = getFormatMapKeyForModeField(field);
  const fieldFormat = fieldFormats[formatKey];
  return resolvePaneTextFormat(
    fieldFormat,
    field.type === Attr.Type.Discrete ? ChartTypes.FieldFormatTypes.Header : ChartTypes.FieldFormatTypes.Axis
  );
}

/**
 * Returns the ChartTypes.TextFormat (or undefined if there is no format) for the specified FieldFormatType.
 */
export function resolveTextFormatByType(
  format: ChartTypes.FieldFormat,
  formatType: ChartTypes.FieldFormatTypes,
  field: EncField
) {
  switch (formatType) {
    case ChartTypes.FieldFormatTypes.Axis:
      return resolveAxisTextFormat(format);
    case ChartTypes.FieldFormatTypes.Header:
      return resolveHeaderTextFormat(format);
    default:
      return resolvePaneTextFormat(
        format,
        field.variable.type === VariableType.DISCRETE
          ? ChartTypes.FieldFormatTypes.Header
          : ChartTypes.FieldFormatTypes.Axis
      );
  }
}

function resolveTextFormat(
  format: ChartTypes.FieldFormat | undefined,
  formatType: ChartTypes.FieldFormatTypes,
  altFormatType: ChartTypes.FieldFormatTypes
) {
  if (!format) {
    return undefined;
  }
  const getFormatForType = (type: ChartTypes.FieldFormatTypes) => {
    switch (type) {
      case ChartTypes.FieldFormatTypes.Axis: {
        return format.axisFormat?.scale?.default;
      }
      case ChartTypes.FieldFormatTypes.Header: {
        return format.headerFormat?.default;
      }
      case ChartTypes.FieldFormatTypes.Pane: {
        return format.paneFormat?.default;
      }
      // falls through
      case ChartTypes.FieldFormatTypes.Legend:
      default: {
        // Same as without a format
        return undefined;
      }
    }
  };
  // Get the format explicitly requested
  const baseTextFormat = getFormatForType(formatType);
  if (baseTextFormat != null && !isAutomaticShortcutType(baseTextFormat.shortcutType)) {
    // Not Auto, so use this format
    return baseTextFormat;
  }
  // If this format is Auto or undefined, see if there is something more specific on the alternative aspect.
  const altTextFormat = getFormatForType(altFormatType);

  if (altTextFormat == null || isAutomaticShortcutType(altTextFormat.shortcutType)) {
    // This format isn't better than the original
    return baseTextFormat;
  }
  return altTextFormat;
}

export function isAutomaticShortcutType(shortcutType: ChartTypes.FormatShortcutType | undefined) {
  return shortcutType === undefined || shortcutType === ChartTypes.FormatShortcutType.Automatic;
}

export function getBaseFormatDisplayName(
  key: string | undefined,
  fieldFormats: ChartTypes.FieldFormatMap | undefined
): string | undefined {
  if (fieldFormats && key) {
    return fieldFormats[key]?.[ChartTypes.FieldFormatTypes.Base]?.[ChartTypes.FieldBaseTypes.DisplayName];
  }
}
